import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Bookmark,
  CircleStop,
  CloudUpload,
  Copy,
  Download,
  FilePlus2,
  HelpCircle,
  Pause,
  PanelLeft,
  Play,
  Radio,
  Share2,
  ShieldCheck,
  Unlink,
  Upload,
  Users
} from "lucide-react";
import type {
  CaptureState,
  MatterDraft,
  TranscriptRow,
  TranscriptSession
} from "../app/ui-types";
import { useLiveSession } from "../hooks/useLiveSession";
import type { LiveShare } from "../hooks/useLiveSession";
import { ConsentDialog } from "./ConsentDialog";
import { recordingAuthorityStatus } from "../domain";
import { displayTranscriptTitle } from "../app/transcript-title";
import { formatTranscriptWallClock } from "../app/transcript-time";

type LegacyLiveCaptureProps = {
  matter: MatterDraft;
  onSessionChange: (session: TranscriptSession) => void;
  onCaptureStateChange: (state: CaptureState) => void;
  onOpenReview: (
    rows: TranscriptRow[],
    session: TranscriptSession | null,
    audioUrl: string | null
  ) => void;
};

type LiveCaptureProps = LegacyLiveCaptureProps & {
  canRecord: boolean;
  onOpenSettings: () => void;
  onTitleChange: (title: string) => void;
  onAuthorityConfirm?: (authority: MatterDraft["authority"]) => void;
  sandbox?: boolean;
  onLiveShareChange?: (share: LiveShare | null) => void;
  matterId?: string;
  previousSessionCount?: number;
};

const stateLabel: Record<CaptureState, string> = {
  ready: "Ready",
  listening: "Listening",
  processing: "Processing",
  interrupted: "Interrupted",
  review: "Review",
  done: "Done"
};

const activeCaptureStates = new Set<CaptureState>([
  "listening",
  "processing"
]);

const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder].map((value) => String(value).padStart(2, "0")).join(":");
};

const formatOffset = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(
    2,
    "0"
  )}`;
};

const EXHIBIT_ACCEPT = "application/pdf,image/jpeg,image/png,image/tiff,image/webp";
const EXHIBIT_TYPES = new Set(EXHIBIT_ACCEPT.split(","));
const EXHIBIT_EXTENSIONS = new Set(["pdf", "jpg", "jpeg", "png", "tif", "tiff", "webp"]);

export const sanitiseExhibitFilename = (name: string) => {
  const cleaned = name
    .normalize("NFC")
    .replace(/[\\/\u0000-\u001f\u007f]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);
  return cleaned || "Untitled exhibit";
};

export const supportedExhibit = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXHIBIT_TYPES.has(file.type) || EXHIBIT_EXTENSIONS.has(extension);
};

function LiveTranscriptRow({
  row,
  recordingStartedAt = null
}: {
  row: TranscriptRow;
  recordingStartedAt?: number | null;
}) {
  const flagged = row.flaggedText;
  const [before, after] = flagged ? row.text.split(flagged) : [row.text, ""];
  const band =
    row.reviewScore < 65 ? "low" : row.reviewScore < 85 ? "medium" : ("high" as const);
  const action = row.kind === "exhibit" || row.kind === "adjournment";

  return (
    <div
      className={`live-line ${row.provisional ? "live-line--provisional" : ""} ${
        action ? "live-line--action" : ""
      }`}
    >
      <span className="line-number">{row.line}</span>
      <button
        className="timecode"
        type="button"
        aria-label={`Recorded at ${
          row.recordedAtMs || recordingStartedAt
            ? formatTranscriptWallClock(
                row.recordedAtMs ?? (recordingStartedAt ?? 0) + row.startMs
              )
            : formatOffset(row.startMs)
        }`}
      >
        {row.recordedAtMs || recordingStartedAt
          ? formatTranscriptWallClock(
              row.recordedAtMs ?? (recordingStartedAt ?? 0) + row.startMs
            )
          : formatOffset(row.startMs)}
      </button>
      <span className="speaker-label">{action ? "" : row.role}</span>
      <p>
        {action ? <strong>{row.text}</strong> : before}
        {!action && flagged && (
          <span
            className={`confidence-word confidence-word--${band}`}
            title={`Review score ${row.reviewScore} of 100`}
          >
            {flagged}
            {band !== "high" && <sup aria-label="Review needed">?</sup>}
          </span>
        )}
        {!action && after}
        {!action && row.provisional && <span className="provisional-caret" aria-hidden="true" />}
      </p>
    </div>
  );
}
export function LiveCapture({
  matter,
  sandbox = false,
  canRecord,
  onOpenSettings,
  onTitleChange,
  onAuthorityConfirm = () => undefined,
  onSessionChange,
  onCaptureStateChange,
  onOpenReview,
  onLiveShareChange,
  matterId,
  previousSessionCount = 0
}: LiveCaptureProps) {
  const session = useLiveSession(
    matter,
    onSessionChange,
    onCaptureStateChange,
    { sandbox, matterId, previousSessionCount }
  );
  const reviewOpened = useRef(false);
  const exhibitInput = useRef<HTMLInputElement | null>(null);
  const liveLinesRef = useRef<HTMLDivElement | null>(null);
  const [markers, setMarkers] = useState<
    Array<{ kind: "adjournment" | "exhibit"; label: string; at: number }>
  >([]);
  const [consentOpen, setConsentOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [followingLatest, setFollowingLatest] = useState(true);
  const [exhibitError, setExhibitError] = useState("");
  const [processingElapsed, setProcessingElapsed] = useState(0);
  const authority = recordingAuthorityStatus(matter);
  useEffect(() => {
    onLiveShareChange?.(session.liveShare);
  }, [onLiveShareChange, session.liveShare]);

  /**
   * Amplitude bars for captured audio, fading into a dotted line for time
   * not yet recorded. Bars are mirrored around the centre line so the trace
   * reads as a waveform rather than a bar chart.
   */
  const traceBars = useMemo(() => {
    const count = 72;
    const energy =
      session.state === "listening"
        ? 0.18 + session.level * 0.82
        : session.state === "processing"
          ? 0.22
          : 0;
    return Array.from({ length: count }, (_, index) => {
      // Newest audio sits at the left and decays rightwards.
      const decay = Math.exp(-index / 9);
      const texture =
        0.55 +
        Math.abs(
          Math.sin(index * 1.31) * 0.5 + Math.sin(index * 0.47 + 0.8) * 0.5
        ) *
          0.45;
      const amplitude = energy * decay * texture;
      return { x: 6 + index * 13, amplitude };
    });
  }, [session.level, session.state]);

  useEffect(() => {
    if (
      session.state !== "review" ||
      reviewOpened.current ||
      !session.transcriptSession
    ) {
      return;
    }
    reviewOpened.current = true;
    const rows = session.rows.map((row) => ({
      ...row,
      recordedAtMs:
        row.recordedAtMs ??
        (session.recordingStartedAt
          ? session.recordingStartedAt + row.startMs
          : undefined)
    }));
    onOpenReview(
      rows,
      {
        ...session.transcriptSession,
        recordedAtEpochMs:
          session.transcriptSession.recordedAtEpochMs ??
          session.recordingStartedAt ??
          undefined
      },
      session.sourceAudioUrl
    );
  }, [
    onOpenReview,
    session.rows,
    session.sourceAudioUrl,
    session.state,
    session.transcriptSession
  ]);

  const lastRowSignature = session.rows.length
    ? `${session.rows.at(-1)?.id}:${session.rows.at(-1)?.text}:${session.rows.at(-1)?.provisional}`
    : "";

  const jumpToLatest = () => {
    const container = liveLinesRef.current;
    if (!container) return;
    setFollowingLatest(true);
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  };

  useLayoutEffect(() => {
    const container = liveLinesRef.current;
    if (!container || !followingLatest || !lastRowSignature) return;
    const frame = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [followingLatest, lastRowSignature]);

  useEffect(() => {
    if (session.state !== "processing") {
      setProcessingElapsed(0);
      return;
    }
    const started = Date.now();
    const update = () => setProcessingElapsed(Math.floor((Date.now() - started) / 1000));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [session.state]);

  const addMarker = (kind: "adjournment" | "exhibit", label: string) => {
    if (session.state === "listening" && !session.paused) {
      session.addMarker(kind, label, session.elapsedSeconds);
    }
    setMarkers((current) => [
      ...current,
      { kind, label, at: session.elapsedSeconds }
    ]);
  };

  const uploadExhibits = (files: FileList | null) => {
    if (!files?.length || session.state !== "listening" || session.paused) return;
    const rejected = Array.from(files).filter((file) => !supportedExhibit(file));
    const accepted = Array.from(files).filter(supportedExhibit);
    accepted.forEach((file) => addMarker("exhibit", sanitiseExhibitFilename(file.name)));
    setExhibitError(
      rejected.length
        ? `${rejected.length} unsupported file${rejected.length === 1 ? " was" : "s were"} not added. Use PDF, JPEG, PNG, TIFF or WebP.`
        : ""
    );
    if (exhibitInput.current) exhibitInput.current.value = "";
  };

  const copyLiveShare = async () => {
    if (!session.liveShare) return;
    try {
      await navigator.clipboard.writeText(session.liveShare.url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch {
      setShareCopied(false);
    }
  };

  return (
    <div className="recorder">
      <header className="recorder__head">
        <div className="recorder__identity">
          <input
            className="recorder-title"
            value={matter.title}
            onChange={(event) => onTitleChange(event.target.value)}
            aria-label="Transcription title"
            placeholder={displayTranscriptTitle("", matter.date)}
          />
          <time dateTime={matter.date}>
            {new Date(`${matter.date}T12:00:00`).toLocaleDateString(undefined, {
              day: "numeric",
              month: "long",
              year: "numeric"
            })}
          </time>
          {sandbox && <span className="sample-chip">Sample matter</span>}
        </div>
        <div className="recorder__signals">
          {session.error && (
            <details className="session-alert">
              <summary
                aria-label="Show recording issue"
                title="Show recording issue"
              >
                <AlertTriangle size={18} aria-hidden="true" />
              </summary>
              <p role="alert">{session.error}</p>
            </details>
          )}
          {activeCaptureStates.has(session.state) && (
            <span
              className={`capture-indicator capture-indicator--${session.state} ${
                session.paused ? "is-paused" : ""
              }`}
              role="status"
              aria-label={session.paused ? "Recording paused" : stateLabel[session.state]}
            >
              <span className="capture-indicator__mark" aria-hidden="true" />
              <span className="capture-indicator__label">
                {session.paused
                  ? "Paused"
                  : session.state === "listening"
                    ? "Recording"
                    : stateLabel[session.state]}
              </span>
            </span>
          )}
        </div>
      </header>

      {session.storageWarning && (
        <section
          className="capture-storage-warning"
          role="status"
          aria-live="polite"
        >
          <AlertTriangle size={18} aria-hidden="true" />
          <p>{session.storageWarning}</p>
        </section>
      )}

      {!sandbox &&
        session.state === "ready" &&
        (!matter.title.trim() || matter.participants.length === 0) && (
          <section className="recording-setup-prompt" role="status">
            <div>
              <strong>Fill in the details to start recording</strong>
              <p>
                Add a title and speaker so the transcript is easier to identify. You can
                still record now after confirming permission.
              </p>
            </div>
            <button className="button button--quiet" type="button" onClick={onOpenSettings}>
              Add details
            </button>
          </section>
        )}

      <section className="live-paper recorder__transcript" aria-labelledby="live-transcript-title">
        <header className="transcript-ruler">
          <h2 id="live-transcript-title">Transcript</h2>
          {session.rows.length > 0 && (
            <span className="confidence-key">
              <i className="confidence-key__high" /> High
              <i className="confidence-key__medium" /> Check
              <i className="confidence-key__low" /> Low
              <details className="confidence-help">
                <summary aria-label="About transcript confidence">
                  <HelpCircle size={15} aria-hidden="true" />
                </summary>
                <p>
                  Confidence estimates transcription certainty. Lower scores mean check
                  source audio. Scores do not measure legal accuracy.
                </p>
              </details>
            </span>
          )}
        </header>
        <div
          className="live-lines"
          ref={liveLinesRef}
          onScroll={(event) => {
            const element = event.currentTarget;
            const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
            setFollowingLatest(distance <= 48);
          }}
        >
          {session.rows.length ? (
            session.rows.map((row) => (
              <LiveTranscriptRow
                row={row}
                recordingStartedAt={session.recordingStartedAt}
                key={row.id}
              />
            ))
          ) : (
            <div className="transcript-empty transcript-empty--quiet">
              {session.state === "ready" && (
                <button
                  className="button button--primary transcript-empty__start"
                  type="button"
                  onClick={() => setConsentOpen(true)}
                >
                  <Radio size={17} aria-hidden="true" />
                  Start recording
                </button>
              )}
            </div>
          )}
        </div>
        {!followingLatest && session.rows.length > 0 && (
          <button className="button button--quiet jump-to-latest" type="button" onClick={jumpToLatest}>
            Jump to latest
          </button>
        )}
      </section>

      {!sandbox &&
        session.liveShare &&
        session.state !== "ready" &&
        session.state !== "review" &&
        session.state !== "done" && (
        <section className="live-share-strip" aria-label="Published live transcript">
          <span className="live-share-strip__status">
            <span className="live-share-strip__dot" aria-hidden="true" />
            Public live transcript
          </span>
          <input
            aria-label="Public live transcript URL"
            value={session.liveShare.url}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
          />
          <button
            className="button button--quiet"
            type="button"
            onClick={() => void copyLiveShare()}
          >
            <Copy size={15} aria-hidden="true" />
            {shareCopied ? "Copied" : "Copy link"}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => void session.revokeLiveShare()}
            disabled={session.liveShareBusy}
          >
            <Unlink size={15} aria-hidden="true" />
            Stop sharing
          </button>
        </section>
      )}

      <section
        className={`recorder__transport recorder__transport--${session.state}`}
        aria-label="Recording controls"
      >
        <strong className="recorder__clock" aria-live="polite">
          {formatTime(session.elapsedSeconds)}
        </strong>
        <div className="acoustic-trace" aria-label="Live microphone level">
          <svg viewBox="0 0 960 64" preserveAspectRatio="none" role="img">
            <title>Microphone waveform</title>
            <line
              x1="0"
              y1="32"
              x2="960"
              y2="32"
              className="trace-baseline"
              strokeDasharray="2 7"
            />
            {traceBars.map((bar, index) => {
              const height = Math.max(1.5, bar.amplitude * 30);
              return (
                <line
                  key={index}
                  x1={bar.x}
                  y1={32 - height}
                  x2={bar.x}
                  y2={32 + height}
                  className="trace-bar"
                />
              );
            })}
          </svg>
        </div>
        <div className="recorder__controls">
          {session.state === "ready" && previousSessionCount > 0 && (
            <p className="session-continuation-note" role="status">
              Session {previousSessionCount + 1} · Previous sessions safely saved
            </p>
          )}
          {session.state === "listening" && (
            <>
              <p className="mobile-capture-note" role="status">
                Keep Dicta open and the screen awake while recording. Calls or app switching may interrupt mobile audio.
              </p>
              <button
                className="button button--quiet transport-action"
                type="button"
                onClick={() => void (session.paused ? session.resume() : session.pause())}
              >
                {session.paused ? (
                  <Play size={16} aria-hidden="true" />
                ) : (
                  <Pause size={16} aria-hidden="true" />
                )}
                {session.paused ? "Resume" : "Pause"}
              </button>
              <button
                className="record-control record-control--single record-control--stop"
                type="button"
                onClick={session.stop}
              >
                <CircleStop size={17} aria-hidden="true" />
                End session
              </button>
            </>
          )}
          {session.state === "processing" && (
            <div className="processing-status" role="status" aria-live="polite">
              <strong>{processingElapsed >= 60 ? "Still working…" : "Preparing review…"}</strong>
              <span>
                This can take a few minutes. Keep this tab open. {formatTime(processingElapsed)}
              </span>
            </div>
          )}
          {session.state === "interrupted" && (
            <div className="recovery-actions">
              {session.recoveryStatus === "server-retry" ? (
                <button
                  className="button button--primary"
                  type="button"
                  onClick={() => void session.retryFinalisation()}
                >
                  <ShieldCheck size={17} aria-hidden="true" />
                  Retry preparing review
                </button>
              ) : (
                <button
                  className="button button--primary"
                  type="button"
                  onClick={session.recover}
                  disabled={!session.recoveryAvailable}
                >
                  <ShieldCheck size={17} aria-hidden="true" />
                  Recover interrupted recording
                </button>
              )}
              <button
                className="button button--quiet"
                type="button"
                onClick={session.resetRecovery}
                disabled={!session.canReleaseRecovery}
              >
                Download evidence and clear recovery
              </button>
            </div>
          )}
        </div>
        {session.pauseWarning && (
          <section className="pause-safety-notice" role="alert">
            <div>
              <strong>Session paused for 10 minutes</strong>
              <p>
                Dicta will end and safely save this session at 20 minutes to protect the
                recording boundary.
              </p>
            </div>
            <button className="button button--quiet" type="button" onClick={session.keepPaused}>
              Keep paused
            </button>
            <button className="button button--primary" type="button" onClick={session.stop}>
              End session now
            </button>
          </section>
        )}
        {session.silenceWarning && !session.paused && (
          <section
            className="silence-prompt"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="silence-prompt-title"
            aria-describedby="silence-prompt-description"
          >
            <div>
              <strong id="silence-prompt-title">Still recording?</strong>
              <p id="silence-prompt-description">
                No speech detected. Dicta pauses in {session.silenceSecondsRemaining} seconds
                unless speech resumes.
              </p>
            </div>
            <button
              className="button button--quiet"
              type="button"
              onClick={session.continueAfterSilence}
            >
              Continue recording
            </button>
            <button
              className="button button--primary"
              type="button"
              onClick={() => void session.pause("silence")}
            >
              Pause now
            </button>
          </section>
        )}
        {(session.state === "listening" || session.state === "processing") && (
          <p className="capture-persistence" role="status" aria-live="polite">
            {session.bufferedChunks > 0 || session.analysisBufferedChunks > 0
              ? "Saved on this device · syncing"
              : session.sourceSafeThroughMs > 0 && session.analysisSafeThroughMs > 0
                ? `Synced through ${formatOffset(
                    Math.min(session.sourceSafeThroughMs, session.analysisSafeThroughMs)
                  )}`
                : "Preparing secure local capture"}
          </p>
        )}
        <div className="marker-strip__actions" aria-label="Markers">
            {!sandbox && session.state === "listening" && !session.liveShare && (
              <button
                className="button button--quiet"
                type="button"
                onClick={() => void session.publishLiveShare()}
                disabled={session.liveShareBusy}
                aria-busy={session.liveShareBusy}
              >
                <Share2 size={15} aria-hidden="true" />
                Publish transcript
              </button>
            )}
            <button
              className="button button--quiet"
              type="button"
              onClick={() => {
                setMarkers((current) => [
                  ...current,
                  { kind: "adjournment", label: "Adjournment", at: session.elapsedSeconds }
                ]);
                void session.pause("adjournment");
              }}
              disabled={session.state !== "listening" || session.paused}
            >
              <Pause size={15} aria-hidden="true" />
              Adjourn
            </button>
            <label
              className={`button button--quiet exhibit-upload ${
                session.state !== "listening" || session.paused ? "is-disabled" : ""
              }`}
            >
              <Upload size={15} aria-hidden="true" />
              Add exhibit
              <input
                ref={exhibitInput}
                type="file"
                multiple
                accept={EXHIBIT_ACCEPT}
                onChange={(event) => uploadExhibits(event.target.files)}
                disabled={session.state !== "listening" || session.paused}
              />
            </label>
        </div>
        {exhibitError && <p className="marker-error" role="alert">{exhibitError}</p>}
      </section>

      {markers.length > 0 && (
        <section className="marker-strip" aria-label="Recording markers">
          <ol className="marker-timeline">
            {markers.map((marker, index) => (
              <li key={`${marker.label}-${marker.at}-${index}`}>
                {marker.kind === "exhibit" ? (
                  <FilePlus2 size={14} aria-hidden="true" />
                ) : (
                  <Bookmark size={14} aria-hidden="true" />
                )}
                <span>{marker.label}</span>
                <time>{formatTime(marker.at)}</time>
              </li>
            ))}
          </ol>
        </section>
      )}

      <ConsentDialog
        open={consentOpen}
        busy={session.isStarting}
        startupStage={session.startupStage}
        requiresCourtApproval={authority.requiresCourtApproval}
        sessionNumber={previousSessionCount + 1}
        participantCount={matter.participants.length}
        glossaryCount={matter.glossary.length}
        onCancel={() => {
          if (session.isStarting) void session.stop();
          setConsentOpen(false);
        }}
        onConfirm={({ courtApproval }) => {
          const nextAuthority: MatterDraft["authority"] = {
            userAttestation: true,
            participantNotice: true,
            participantConsent: true,
            confidentialityReviewed: true,
            courtApproval
          };
          onAuthorityConfirm(nextAuthority);
          void Promise.resolve(session.start(nextAuthority)).finally(() => {
            setConsentOpen(false);
          });
        }}
      />
    </div>
  );
}
